<script lang="ts" setup>
import { z } from 'zod';
import { reactive, ref, useAuth, useDialog, useI18n, useToast } from '#imports';
const { t } = useI18n();

const emit = defineEmits(['toggle-auth-step']);

// Login form
const loginModel = reactive({
  email: '',
  password: '',
});

const loginSchema = z.object({
  email: z
    .string({ message: 'Email is required' })
    .email({ message: 'Please enter a valid email address' }),
  password: z
    .string({ message: 'Password is required' })
    .min(8, { message: 'Password must be at least 8 characters long' })
    .max(255, { message: 'Password must be at most 255 characters long' }),
});

const { showError } = useToast();
const { closeDialog } = useDialog();
const { login, currentUser } = useAuth();
const loginFailed = ref(false);
const isLoading = ref(false);
const handleLogin = async () => {
  const validationResult = loginSchema.safeParse(loginModel);
  if (!validationResult.success) return;
  isLoading.value = true;
  await login(loginModel.email, loginModel.password);
  if (currentUser.value) {
    await closeDialog();
  } else {
    loginFailed.value = true;
  }
  isLoading.value = false;
};
</script>

<template>
  <MPMessage v-if="loginFailed" version="error">
    {{ t('auth.login.loginFailed') }}
  </MPMessage>
  <form class="login" @submit.prevent="handleLogin">
    <div>
      <MPInput
        v-model="loginModel.email"
        name="email"
        :label="t('auth.login.email')"
        type="email"
        :placeholder="t('auth.login.email')"
        class="input-email"
        :validation="loginSchema.shape.email"
        required
        hide-label
      />
    </div>
    <div>
      <MPInput
        v-model="loginModel.password"
        name="password"
        type="password"
        :label="t('auth.login.password')"
        :placeholder="t('auth.login.password')"
        :validation="loginSchema.shape.password"
        toggle-mask
        hide-label
        required
        autocomplete
      />
    </div>
    <MPButton
      type="submit"
      :disabled="isLoading"
      :is-loading="isLoading"
      :label="t('auth.login.title')"
    />
    <MPButton
      label="Join"
      :disabled="isLoading"
      version="secondary"
      @click="emit('toggle-auth-step')"
    />
    <NuxtLinkLocale to="forgot-password">
      {{ t('auth.login.forgot') }}
    </NuxtLinkLocale>
  </form>
</template>

<style lang="postcss" scoped>
.login {
  display: grid;
  gap: 1rem;
}
</style>
